import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  Switch,
  Stack,
} from '@mui/material';
import { Close, Send, Email, Lock, Edit, VisibilityOff } from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { sendSecureEmail, sendBulkSecureEmail } from '../../services/messages';


/**
 * SendEmailDialog
 *
 * Single-study mode:  pass `study` (object)
 * Bulk mode:          pass `studies` (array of study objects)
 */
const SendEmailDialog = ({
  open,
  onClose,
  study,          // single study object
  studies,        // array of study objects (bulk mode)
  defaultReportVersion = 'original',
  hasWorkingCopies = false,
  patientEmail,   // pre-resolved from patient record
}) => {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const cardBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)';

  const isBulk = Array.isArray(studies) && studies.length > 0;
  const effectiveStudies = isBulk ? studies : (study ? [study] : []);

  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailAutoFilled, setEmailAutoFilled] = useState(false);
  const [note, setNote] = useState('');
  const [sendImage, setSendImage] = useState(false);
  const [sendReport, setSendReport] = useState(false);
  const [anonymize, setAnonymize] = useState(false);
  const [reportVersion, setReportVersion] = useState(defaultReportVersion);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  useEffect(() => {
    setReportVersion(defaultReportVersion);
  }, [defaultReportVersion, open]);

  // Auto-populate recipient email from patient record when dialog opens
  useEffect(() => {
    if (!open) return;
    if (patientEmail && !recipientEmail) {
      setRecipientEmail(patientEmail);
      setEmailAutoFilled(true);
    }
  }, [open, patientEmail]);

  const handleClose = () => {
    if (loading) return;
    setRecipientEmail('');
    setEmailAutoFilled(false);
    setNote('');
    setSendImage(false);
    setSendReport(false);
    setAnonymize(false);
    setError('');
    setSuccess(false);
    setBulkResults(null);
    onClose();
  };

  const handleSend = async () => {
    if (!recipientEmail.trim()) { setError('Please enter a recipient email'); return; }
    if (!sendImage && !sendReport) { setError('Select at least one: Send Images or Send Reports'); return; }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail.trim())) { setError('Please enter a valid email address'); return; }

    setError('');
    setLoading(true);
    setSuccess(false);
    setBulkResults(null);

    try {
      if (isBulk) {
        const result = await sendBulkSecureEmail({
          studyIds: effectiveStudies.map(s => s.pacsStudyId || s._id),
          recipientEmail: recipientEmail.trim(),
          note: note.trim(),
          sendImage,
          sendReport,
          anonymize,
          reportVersion,
          language: 'en',
          simplify: false,
        });
        setBulkResults(result);
        setSuccess(true);
      } else {
        const result = await sendSecureEmail({
          studyId: effectiveStudies[0].pacsStudyId || effectiveStudies[0]._id,
          recipientEmail: recipientEmail.trim(),
          note: note.trim(),
          sendImage,
          sendReport,
          anonymize,
          reportVersion,
          language: 'en',
          simplify: false,
          patientBirthDate: singleStudy?.patientBirthDate || effectiveStudies[0]?.patientBirthDate || null,
        });
        if (result.success) {
          setSuccess(true);
          setTimeout(handleClose, 2000);
        } else {
          setError(result.error || 'Failed to send email');
        }
      }
    } catch (err) {
      const details = err.response?.data?.details ? ` (${err.response.data.details})` : '';
      const msg = (err.response?.data?.error || err.message || 'Failed to send email') + details;
      if (err.response?.data?.requiresEmailConnection) {
        setError(`${msg} Please connect your facility email account first.`);
        setTimeout(() => { navigate('/email-connection'); }, 3000);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const singleStudy = !isBulk && effectiveStudies[0];
  const hasReports = singleStudy
    ? (singleStudy?.selfReportStatus === 'completed' || singleStudy?.dycovueReportStatus === 'completed')
    : true; // in bulk mode let the backend decide per study

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: cardBg,
          color: textColorPrimary,
          borderRadius: '12px',
          border: cardBorder,
          width: { xs: '95vw', sm: '760px', md: '820px' },
          maxWidth: '840px !important',
          boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ px: 2, pt: 1.25, pb: 1, borderBottom: cardBorder, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Email color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '0.95rem' }}>
            {isBulk ? `Send Secure Email (${effectiveStudies.length} Studies)` : 'Send Secure Email'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={loading} size="small" sx={{ p: '3px', color: textColorSecondary }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75, alignItems: 'start' }}>
          {/* Left Column: Study Summary & Recipient Info */}
          <Box>
            {/* Study summary */}
            {isBulk ? (
              <Box mb={1}>
                <Typography variant="caption" fontWeight={700} sx={{ color: textColorSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }} gutterBottom>
                  Selected studies ({effectiveStudies.length}):
                </Typography>
                <List dense disablePadding sx={{ maxHeight: 95, overflowY: 'auto', border: cardBorder, borderRadius: '8px', mt: 0.5 }}>
                  {effectiveStudies.map((s) => (
                    <ListItem key={s._id} divider sx={{ py: 0.25, px: 1 }}>
                      <ListItemText
                        primary={anonymize ? 'Anonymous Patient' : (s.patientName || s.patientId || 'Unknown')}
                        secondary={`${s.modality || '—'} · ${s.studyDescription || 'Unnamed'}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600, fontSize: '0.74rem', color: textColorPrimary }}
                        secondaryTypographyProps={{ variant: 'caption', fontSize: '0.68rem', color: textColorSecondary }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : singleStudy && (
              <Box mb={1} p={1} sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC', borderRadius: '8px', border: cardBorder }}>
                <Stack spacing={0.25}>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', color: textColorPrimary }}>
                    <strong>Patient:</strong> {anonymize ? 'Anonymous Patient' : (singleStudy.patientName || singleStudy.patientId)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', color: textColorPrimary }}>
                    <strong>Study:</strong> {singleStudy.modality || 'N/A'} — {singleStudy.studyDescription || 'Unnamed Study'}
                  </Typography>
                </Stack>
              </Box>
            )}

            {/* Recipient — auto-filled indicator */}
            {!isBulk && emailAutoFilled && (
              <Alert severity="info" icon={<Email sx={{ fontSize: 15 }} />} sx={{ mb: 0.75, py: 0.15, px: 1, fontSize: '0.72rem', '& .MuiAlert-message': { py: 0 } }}>
                Auto-filled from patient record (editable)
              </Alert>
            )}
            {!isBulk && !emailAutoFilled && !recipientEmail && (
              <Alert severity="warning" sx={{ mb: 0.75, py: 0.15, px: 1, fontSize: '0.72rem', '& .MuiAlert-message': { py: 0 } }}>
                No email on file. Please enter one below.
              </Alert>
            )}

            <TextField
              fullWidth
              size="small"
              label="Recipient Email Address"
              type="email"
              value={recipientEmail}
              onChange={(e) => { setRecipientEmail(e.target.value); setEmailAutoFilled(false); }}
              margin="dense"
              required
              disabled={loading}
              helperText={emailAutoFilled ? 'From patient record — edit to override' : 'Enter recipient email'}
              FormHelperTextProps={{ sx: { fontSize: '0.68rem', mx: 0.5, mt: 0.2 } }}
              InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
              sx={{
                mb: 0.75,
                '& .MuiInputBase-root': {
                  fontSize: '0.8rem',
                  height: 32,
                  color: textColorPrimary,
                  bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
                }
              }}
            />

            {/* Note */}
            <TextField
              fullWidth
              size="small"
              label="Note (Optional)"
              multiline
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              margin="dense"
              disabled={loading}
              helperText="Personal note included in the secure email"
              inputProps={{ maxLength: 2000 }}
              FormHelperTextProps={{ sx: { fontSize: '0.68rem', mx: 0.5, mt: 0.2 } }}
              InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '0.8rem',
                  color: textColorPrimary,
                  bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
                }
              }}
            />
          </Box>

          {/* Right Column: Content Selection, Anonymize, & Password */}
          <Box display="flex" flexDirection="column" gap={1}>
            {/* Content selection */}
            <Box p={1} sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC', borderRadius: '8px', border: cardBorder }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: textColorSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                Select Content to Send:
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={sendImage} onChange={(e) => setSendImage(e.target.checked)} disabled={loading} sx={{ py: 0.25 }} />}
                  label={<Typography variant="body2" sx={{ fontSize: '0.78rem', color: textColorPrimary }}>Send Images</Typography>}
                />

                <FormControlLabel
                  control={<Checkbox size="small" checked={sendReport} onChange={(e) => setSendReport(e.target.checked)} disabled={loading || (!isBulk && !hasReports)} sx={{ py: 0.25 }} />}
                  label={<Typography variant="body2" sx={{ fontSize: '0.78rem', color: textColorPrimary }}>Send Report(s)</Typography>}
                />
              </Stack>

              {!isBulk && !hasReports && (
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.25, fontSize: '0.7rem' }}>
                  No completed reports available for this study
                </Typography>
              )}

              {sendReport && (isBulk || hasReports) && (
                <Box sx={{ mt: 0.75, p: 0.75, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF', borderRadius: '6px', border: cardBorder }}>
                  <FormControl size="small">
                    <FormLabel sx={{ fontSize: '0.72rem', mb: 0.2, color: textColorSecondary }}>Report Version:</FormLabel>
                    <RadioGroup row value={reportVersion} onChange={(e) => setReportVersion(e.target.value)}>
                      <FormControlLabel value="original" control={<Radio size="small" sx={{ p: 0.5 }} />}
                        label={<Box display="flex" alignItems="center" gap={0.5}><Lock sx={{ fontSize: 13 }} color="action" /><Typography variant="body2" sx={{ fontSize: '0.74rem' }}>Original (Locked)</Typography></Box>}
                      />
                      {hasWorkingCopies && (
                        <FormControlLabel value="working" control={<Radio size="small" sx={{ p: 0.5 }} />}
                          label={<Box display="flex" alignItems="center" gap={0.5}><Edit sx={{ fontSize: 13 }} color="warning" /><Typography variant="body2" sx={{ fontSize: '0.74rem' }}>Editable Version</Typography></Box>}
                        />
                      )}
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
            </Box>

            {/* Anonymize toggle */}
            <Box display="flex" alignItems="center" justifyContent="space-between" p={0.85} sx={{ bgcolor: anonymize ? (isDark ? 'rgba(245,158,11,0.1)' : '#FEF3C7') : (isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC'), borderRadius: '8px', border: '1px solid', borderColor: anonymize ? 'warning.light' : cardBorder }}>
              <Box display="flex" alignItems="center" gap={0.75}>
                <VisibilityOff sx={{ color: anonymize ? 'warning.main' : 'text.disabled', fontSize: 16 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Remove image header identifiers</Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.68rem', color: textColorSecondary, display: 'block' }}>
                    {'Reports keep patient details; image pixels may contain identifying text.'}
                  </Typography>
                </Box>
              </Box>
              <Switch checked={anonymize} onChange={(e) => setAnonymize(e.target.checked)} disabled={loading} color="warning" size="small" />
            </Box>

            <Alert severity="info">The recipient requests an email verification code through the shared link. Access expires and can be revoked.</Alert>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1, py: 0.25, px: 1.25, fontSize: '0.75rem' }} onClose={() => setError('')}>{error}</Alert>
        )}

        {success && !bulkResults && (
          <Alert severity="success" sx={{ mt: 1, py: 0.25, px: 1.25, fontSize: '0.75rem' }}>Email sent successfully!</Alert>
        )}

        {/* Bulk results summary */}
        {bulkResults && (
          <Box mt={1}>
            <Alert severity={bulkResults.failed === 0 ? 'success' : bulkResults.sent === 0 ? 'error' : 'warning'} sx={{ py: 0.25, px: 1.25, fontSize: '0.75rem' }}>
              {bulkResults.sent}/{bulkResults.total} emails sent successfully.
              {bulkResults.failed > 0 && ` ${bulkResults.failed} failed.`}
            </Alert>
            {bulkResults.results?.some(r => !r.success) && (
              <List dense sx={{ mt: 0.5 }}>
                {bulkResults.results.filter(r => !r.success).map((r) => (
                  <ListItem key={r.studyId} disableGutters sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={<Typography variant="caption" color="error">Study {r.studyId}: {r.error}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, borderTop: cardBorder }}>
        {bulkResults ? (
          <Button onClick={handleClose} variant="contained" sx={{ height: 30, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 2 }}>Close</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading} sx={{ height: 30, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 1.5 }}>Cancel</Button>
            <Button
              onClick={handleSend}
              variant="contained"
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Send sx={{ fontSize: 15 }} />}
              disabled={loading || !recipientEmail.trim() || (!sendImage && !sendReport)}
              sx={{
                height: 30,
                fontSize: '0.8rem',
                borderRadius: '6px',
                textTransform: 'none',
                px: 1.75,
                bgcolor: '#0D9488',
                '&:hover': { bgcolor: '#0F766E' }
              }}
            >
              {loading ? 'Sending...' : isBulk ? `Send to ${effectiveStudies.length} Studies` : 'Send Email'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SendEmailDialog;
